import { type UserListItem } from '@gb-ticket/shared-types';
import { ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router';

import { useVoiceCopy } from '@/platform/voice/use-voice';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { adminPanelClassName } from '@/platform/features/admin-panel';
import { RoleAssignmentBadges } from '@/platform/features/users/role-assignment-badges';
import { type UserSelection } from '@/platform/features/users/users-table';
import { userAdminPath } from '@/platform/lib/admin';
import { PersonChip } from '@/platform/components/person-chip';

/** Phone layout of the user list (below 640 px); the table takes over from `sm`. */
export function UsersCardList({
  items,
  canWrite,
  selection,
}: {
  items: UserListItem[];
  canWrite: boolean;
  selection?: UserSelection;
}) {
  const copy = useVoiceCopy();
  if (items.length === 0) {
    return (
      <div className={`${adminPanelClassName} text-center text-sm text-muted-foreground`}>
        <p>Keine Benutzer gefunden.</p>
        <p className="mt-1 text-xs">{copy('platform.users.filters.reset')}</p>
      </div>
    );
  }

  return (
    <ul className="space-y-2" aria-label="Benutzer">
      {items.map((item) => {
        const org = [item.department?.name, item.team?.name].filter(Boolean).join(' · ');
        return (
          <li
            key={item.id}
            className={cn(
              'flex items-start rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-colors hover:border-primary',
              !item.isActive && 'opacity-70',
            )}
          >
            {selection ? (
              <label className="p-4 pr-0" onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  checked={selection.selectedIds.has(item.id)}
                  aria-label={`${item.displayName} auswählen`}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={(checked) => selection.onToggle(item.id, checked === true)}
                />
              </label>
            ) : null}
            <Link
              to={userAdminPath(item.id)}
              aria-label={`${item.displayName} ${canWrite ? 'bearbeiten' : 'ansehen'}`}
              className="flex min-w-0 flex-1 items-start gap-3 p-4 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <PersonChip
                    className="font-medium"
                    firstName={item.firstName}
                    lastName={item.lastName}
                    displayName={item.displayName}
                    profilePhotoUrl={item.profilePhotoUrl}
                  />
                  {item.isActive ? null : <Badge variant="destructive">Deaktiviert</Badge>}
                </div>
                <p className="truncate text-sm text-muted-foreground">{item.email}</p>
                {org ? <p className="truncate text-sm">{org}</p> : null}
                <RoleAssignmentBadges roles={item.roleAssignments} />
              </div>
              <ChevronRightIcon
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
